import type { FeatureAction } from '@/core/security';
import { RFQ_FEATURE } from './rfq.constants';

/**
 * Which BID controls the signed-in role may be offered.
 *
 * The screens' vocabulary is create / edit / duplicate / hold / resume / extend / force close / withdraw /
 * delete; the platform's is the four actions of the RBAC matrix, and the guard on each `/rfqs` endpoint
 * follows the HTTP verb it is reached by (`@perm.on('Request For Quotation', …)`). This is the one place the
 * two are lined up -- the same job `masterAccess.ts` and `templateAccess.ts` do for their modules, and
 * deliberately the same shape.
 *
 * - **Create** (`POST /rfqs`, `POST /rfqs/{id}/duplicate`): Create BID, Use template, Duplicate. Duplicate is a
 *   create -- it POSTs a new draft -- so a role that may update but not create is not offered it.
 * - **Update** (`PUT` / lifecycle `POST`s): Edit, Put On Hold, Resume, Extend Deadline, Force Close, a supplier's
 *   Withdraw, and every save of the wizard after the draft exists.
 * - **Delete** (`DELETE /rfqs/{id}`): Delete, offered on drafts only.
 *
 * View needs no test of its own: every route is already behind VIEW on the feature (`RequireFeature` in
 * `routes.tsx`), so a role that reached a screen can read it.
 */
export interface RfqCapabilities {
  create: boolean;
  update: boolean;
  delete: boolean;
}

/** The action each route of the module needs beyond VIEW; `undefined` means VIEW alone. */
export const RFQ_ROUTE_ACTION = {
  list: undefined,
  templates: undefined,
  detail: undefined,
  /** A new BID starts with `POST /rfqs`. */
  create: 'create',
  /** An existing draft (or a fresh duplicate) is saved with `PUT`s. */
  edit: 'update',
} as const satisfies Record<string, FeatureAction | undefined>;

/**
 * What this role may do on BIDs.
 *
 * `can` is `useFeatureAccess().can`, which answers `true` for every action while the grants are **unknown** --
 * the same deliberate direction the other modules take: the client is not a security boundary, and the
 * platform refuses whatever the grants do not allow.
 */
export function rfqCapabilities(can: (feature: string, action: FeatureAction) => boolean): RfqCapabilities {
  return {
    create: can(RFQ_FEATURE, 'create'),
    update: can(RFQ_FEATURE, 'update'),
    delete: can(RFQ_FEATURE, 'delete'),
  };
}
